/* ----------------------------------------------------------------------------
   M1 · Agreement History drawer — live version. Renders the parent's agreement
   timeline (GET .../agreement/timeline/{parentId}, fetched once per parent by
   the master page and passed in) filtered down to the agreements that govern
   one courier code. Warnbox summary + dl facts + stage-aware timeline; footer
   deep-links to the Agreement Repository.
   ---------------------------------------------------------------------------- */
import React from 'react'
import PropTypes from 'prop-types'
import {useNavigate} from 'react-router-dom'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {SideDrawer, Badge, fmtDate} from '../components'
import {AGREEMENT_TYPE_LABEL} from '../supplyMeta'
import {pick, itemsForCode, codeAgreementInfo} from './codeData'

function typeLabel(a) {
  const t = pick(a, 'type')
  if (t === 'COMMERCIAL_ADDENDUM' || t === 'OPERATIONAL_ADDENDUM') {
    const no = pick(a, 'addendumNo', 'addendum_no')
    return (AGREEMENT_TYPE_LABEL[t] || t) + (no != null ? ' #' + no : '')
  }
  return AGREEMENT_TYPE_LABEL[t] || t || 'Agreement'
}

function uploadedMeta(a) {
  const docs = pick(a, 'documents') || []
  const first = docs[0] || {}
  const by = pick(first, 'uploadedBy', 'uploaded_by') || pick(a, 'createdBy', 'created_by')
  const at = pick(first, 'uploadedAt', 'uploaded_at') || pick(a, 'createdDate', 'created_date')
  if (!by && !at) return ''
  return ' · Uploaded' + (by ? ' by ' + by : '') + (at ? ' on ' + fmtDate(at) : '')
}

export default function AgreementHistoryDrawer({code, timeline, onClose}) {
  const navigate = useNavigate()
  const c = code

  const loading = !!(c && c.parentId && !timeline)
  const loadError = timeline && timeline.error
  const items = (timeline && timeline.items) || []

  const mine = c
    ? itemsForCode(items, c).sort(
        (x, y) =>
          new Date(pick(y.agreement || {}, 'effectiveFrom', 'effective_from') || 0) -
          new Date(pick(x.agreement || {}, 'effectiveFrom', 'effective_from') || 0)
      )
    : []
  const info = c ? codeAgreementInfo(items, c) : null
  const boxCls = info ? (info.key === 'signed' ? 'ok' : info.key === 'norecord' || info.key === 'expired' ? 'err' : 'info') : 'info'
  const BoxIcon =
    info && info.key === 'signed'
      ? CheckCircleOutlinedIcon
      : info && (info.key === 'norecord' || info.key === 'expired')
      ? ErrorOutlineIcon
      : InfoOutlinedIcon

  return (
    <SideDrawer
      open={!!c}
      onClose={onClose}
      title={c ? 'Agreement History — ' + c.code : ''}
      width={620}
      footer={
        <>
          <button className='btn btn-ghost' onClick={onClose}>
            Close
          </button>
          <button
            className='btn btn-primary'
            onClick={() => {
              onClose()
              navigate('/supply/agreements')
            }}
          >
            <OpenInNewIcon style={{width: 15, height: 15}} />
            Open Agreement Repository
          </button>
        </>
      }
    >
      {c ? (
        !c.parentId ? (
          <div className='warnbox info'>
            <InfoOutlinedIcon />
            <div>This legacy code is not linked to an onboarded parent — no agreement timeline available.</div>
          </div>
        ) : loading ? (
          <div className='warnbox info'>
            <InfoOutlinedIcon />
            <div>Loading agreement timeline…</div>
          </div>
        ) : loadError ? (
          <div className='warnbox err'>
            <ErrorOutlineIcon />
            <div>Failed to load agreement timeline: {loadError}</div>
          </div>
        ) : (
          <>
            <div className={'warnbox ' + boxCls} style={{marginBottom: 14}}>
              <BoxIcon />
              <div>{info.tip}</div>
            </div>

            <div className='dl' style={{gridTemplateColumns: 'auto 1fr', maxWidth: 520, marginBottom: 16}}>
              <dt>Courier code</dt>
              <dd>
                <span className='mono'>{c.code}</span>
              </dd>
              <dt>Parent courier</dt>
              <dd>{c.parent || '—'}</dd>
              <dt>Vertical</dt>
              <dd>
                <span className={'chip chip-' + String(c.vertical || 'd2c').toLowerCase()}>{c.vertical || 'D2C'}</span>
              </dd>
              <dt>Current status</dt>
              <dd>
                <Badge status={info.key} title={info.tip} />
              </dd>
            </div>

            {mine.length ? (
              <>
                <div className='seclabel' style={{marginBottom: 10}}>
                  Agreement history for this code
                </div>
                <div className='timeline'>
                  {mine.map((it, idx) => {
                    const a = it.agreement || {}
                    const signed = pick(a, 'stage') === 'SIGNED'
                    const from = pick(a, 'effectiveFrom', 'effective_from')
                    const to = pick(a, 'effectiveTo', 'effective_to')
                    const status = pick(a, 'status')
                    const verts = pick(a, 'verticals') || []
                    const appliesAll = pick(a, 'appliesToAll', 'applies_to_all')
                    const linked = pick(a, 'linkedCpAccountCodes', 'linked_cp_account_codes') || []
                    return (
                      <div key={pick(a, 'id', '_id') || idx} className={'tl-item ' + (signed ? 'green' : 'amber')}>
                        <div className='tl-card'>
                          <div
                            className='row between center wrap'
                            style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}
                          >
                            <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                              <b>{typeLabel(a)}</b>
                              <Badge
                                status={signed ? 'signed' : 'pending'}
                                label={signed ? 'Stage 2: Signed' : 'Stage 1: Email received'}
                              />
                              {status && status !== 'ACTIVE' ? <Badge status={status} label={status} /> : null}
                              {it.expired ? <Badge status='expired' /> : null}
                              {verts.length ? <span className='tag chip-blue'>{verts.join('/')}</span> : null}
                            </div>
                            <span className='muted' style={{fontSize: 12}}>
                              {from ? 'Effective ' + fmtDate(from) : ''}
                              {to ? ' → ' + fmtDate(to) : ' · open-ended'}
                            </span>
                          </div>
                          <div style={{fontSize: 13.5, marginTop: 8, fontWeight: 500}}>{pick(a, 'title')}</div>
                          <div className='tl-meta'>
                            {appliesAll
                              ? 'Applies to: All ' + (pick(a, 'parentName', 'parent_name') || c.parent || '') + ' codes'
                              : 'Applies to: ' + linked.join(', ')}
                            {uploadedMeta(a)}
                            {!signed && status !== 'REJECTED' && status !== 'SUPERSEDED' ? (
                              <span style={{color: 'var(--warning)'}}> · ⚠ Signing pending</span>
                            ) : null}
                            {it.expiresInDays != null ? (
                              <span style={{color: 'var(--warning)'}}>{' · Expires in ' + it.expiresInDays + ' days'}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
            ) : (
              <div className='warnbox err'>
                <ErrorOutlineIcon />
                <div>No agreement on file for this code.</div>
              </div>
            )}
          </>
        )
      ) : null}
    </SideDrawer>
  )
}

AgreementHistoryDrawer.propTypes = {
  code: PropTypes.object,
  timeline: PropTypes.object,
  onClose: PropTypes.func,
}
